import React, { useLayoutEffect, useRef } from 'react';
import { getAbilityFlyIconComponent } from '../ability_display/abilityFlyIcon';
import { scheduleAbilityGainSoundForBatch } from '../ability_display/playAbilityGainSound';
import './ArranAbilityStealFlyOverlay.css';

const START_FRAC = 0.12;
const FLIGHT_MS = 720;
const ICON_SIZE = 72;

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
}

function scaleAlongArc(progress) {
  const peak = Math.sin(Math.PI * progress);
  return START_FRAC + (1 - START_FRAC) * peak;
}

function getBattlefieldSlotCenterPixels(zone, index) {
  if (typeof document === 'undefined' || !zone || index == null) {
    return null;
  }
  const slotElement = document.querySelector(
    `[data-slot-zone="${zone}"][data-slot-index="${index}"]`
  );
  if (!slotElement) {
    return null;
  }
  const rect = slotElement.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * Stolen ability icon flies source unit → Arr'an, then disappears.
 */
export const ArranAbilityStealFlyOverlay = ({
  grant,
  abilityName,
  onComplete,
}) => {
  const overlayRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const flightCompletedRef = useRef(false);
  const soundPlayedRef = useRef(false);

  useLayoutEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const grantKey = grant?.key ?? '';
  const fromZone = grant?.fromZone ?? '';
  const fromIndex = grant?.fromIndex;
  const toZone = grant?.toZone ?? '';
  const toIndex = grant?.toIndex;

  useLayoutEffect(() => {
    flightCompletedRef.current = false;
    soundPlayedRef.current = false;

    if (!fromZone || fromIndex == null || !toZone || toIndex == null || !abilityName) {
      return undefined;
    }

    const root = overlayRef.current;
    if (!root) {
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;
    let startTime = null;
    let cachedStart = null;
    let measureAttempts = 0;
    let warmupFrames = 0;

    const completeOnce = () => {
      if (flightCompletedRef.current || cancelled) {
        return;
      }
      flightCompletedRef.current = true;
      onCompleteRef.current?.();
    };

    root.style.opacity = '0';
    root.style.pointerEvents = 'none';

    const tick = (now) => {
      if (cancelled) {
        return;
      }

      if (warmupFrames < 2) {
        warmupFrames += 1;
        rafId = requestAnimationFrame(tick);
        return;
      }

      if (!cachedStart) {
        cachedStart = getBattlefieldSlotCenterPixels(fromZone, fromIndex);
      }
      const targetCenter = getBattlefieldSlotCenterPixels(toZone, toIndex);

      if (!cachedStart || !targetCenter) {
        measureAttempts += 1;
        if (measureAttempts < 28) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        completeOnce();
        return;
      }

      if (startTime === null) {
        startTime = now;
      }

      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / FLIGHT_MS);
      const e = easeInOutCubic(t);
      const x = cachedStart.x + (targetCenter.x - cachedStart.x) * e;
      const y = cachedStart.y + (targetCenter.y - cachedStart.y) * e;
      const scale = scaleAlongArc(t);

      if (t >= 0.92 && !soundPlayedRef.current) {
        soundPlayedRef.current = true;
        scheduleAbilityGainSoundForBatch(
          [{ ability: abilityName, delayMs: 0 }],
          grantKey
        );
      }

      root.style.position = 'fixed';
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      root.style.transform = `translate(-50%, -50%) scale(${scale})`;
      root.style.transformOrigin = 'center center';
      root.style.opacity = t < 0.05
        ? String(t / 0.05)
        : t > 0.88
          ? String(Math.max(0, 1 - (t - 0.88) / 0.12))
          : '1';
      root.style.zIndex = '100002';

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        completeOnce();
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [grantKey, fromZone, fromIndex, toZone, toIndex, abilityName]);

  const IconComponent = getAbilityFlyIconComponent(abilityName);
  if (!grant || !IconComponent) {
    return null;
  }

  return (
    <div ref={overlayRef} className="arran-ability-steal-fly-overlay" aria-hidden="true">
      <div className="arran-ability-steal-fly-overlay__icon-shell">
        <IconComponent size={ICON_SIZE} />
      </div>
    </div>
  );
};

export default ArranAbilityStealFlyOverlay;
